import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import type { SocialEngagementQueueItem, SocialListeningQuery } from '@modules/shared-types';
import { SocialEngagementService } from '../../services/SocialEngagementService';

export interface SocialEngagementPanelProps {
  projectId: string;
}

type PendingPreview = { kind: 'refresh'; queryId: string; data: any } | { kind: 'reply'; itemId: string; data: any };
type AnalyticsGroupBy = 'pillar' | 'series' | 'audience' | 'post_type';

export const SocialEngagementPanel: React.FC<SocialEngagementPanelProps> = ({ projectId }) => {
  const service = useMemo(() => new SocialEngagementService(), []);
  const [queries, setQueries] = useState<SocialListeningQuery[]>([]);
  const [items, setItems] = useState<SocialEngagementQueueItem[]>([]);
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queryDialog, setQueryDialog] = useState(false);
  const [queryDraft, setQueryDraft] = useState({ name: '', kind: 'search', query: '', trackedUsername: '' });
  const [approvalItem, setApprovalItem] = useState<SocialEngagementQueueItem | null>(null);
  const [replyText, setReplyText] = useState('');
  const [preview, setPreview] = useState<PendingPreview | null>(null);
  const [analyticsGroupBy, setAnalyticsGroupBy] = useState<AnalyticsGroupBy>('pillar');
  const [analyticsComparison, setAnalyticsComparison] = useState<{
    groups: Array<{ key: string; publicationCount: number; metrics: Record<string, number>; engagementRate?: number }>;
    publicationsAnalyzed: number;
  }>({ groups: [], publicationsAnalyzed: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextQueries, nextItems, nextComparison] = await Promise.all([
        service.listQueries(projectId),
        service.listEngagement(projectId, status || undefined),
        service.analyticsComparison(projectId, analyticsGroupBy),
      ]);
      setQueries(nextQueries);
      setItems(nextItems);
      setAnalyticsComparison(nextComparison);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load social engagement data');
    } finally {
      setLoading(false);
    }
  }, [analyticsGroupBy, projectId, service, status]);

  useEffect(() => { void load(); }, [load]);

  const run = async (action: () => Promise<void>) => {
    setWorking(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Social operation failed');
    } finally {
      setWorking(false);
    }
  };

  const saveQuery = () => run(async () => {
    await service.saveQuery(projectId, {
      ...queryDraft,
      trackedUsername: queryDraft.trackedUsername || undefined,
      query: queryDraft.query || undefined,
      enabled: true,
    });
    setQueryDialog(false);
    setQueryDraft({ name: '', kind: 'search', query: '', trackedUsername: '' });
    await load();
  });

  const previewRefresh = (queryId: string) => run(async () => {
    const data = await service.refreshQuery(projectId, queryId, { maxPages: 1, limit: 50, maxCostUsd: 0.25, dryRun: true });
    setPreview({ kind: 'refresh', queryId, data });
  });

  const confirmRefresh = (queryId: string) => run(async () => {
    await service.refreshQuery(projectId, queryId, { maxPages: 1, limit: 50, maxCostUsd: 0.25 });
    setPreview(null);
    await load();
  });

  const approve = () => approvalItem && run(async () => {
    await service.approve(projectId, approvalItem.id, replyText);
    setApprovalItem(null);
    setReplyText('');
    await load();
  });

  const previewReply = (item: SocialEngagementQueueItem) => run(async () => {
    const data = await service.reply(projectId, item.id, { dryRun: true, maxCostUsd: 0.25 });
    setPreview({ kind: 'reply', itemId: item.id, data });
  });

  const confirmReply = (itemId: string, confirmation: string) => run(async () => {
    await service.reply(projectId, itemId, { confirm: confirmation, maxCostUsd: 0.25 });
    setPreview(null);
    await load();
  });

  const reconcileReply = (itemId: string) => run(async () => {
    await service.reconcileReply(projectId, itemId, { maxCostUsd: 0.25 });
    await load();
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Stack spacing={3} data-testid="content-plan-engagement-panel">
      <Box>
        <Typography variant="h6">X Engagement</Typography>
        <Typography variant="body2" color="text.secondary">
          Research and review only. X is contacted only when you press Refresh now or explicitly send an approved reply.
        </Typography>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {preview?.kind === 'refresh' && (
        <Alert data-testid="engagement-refresh-preview" severity="info" action={<Button data-testid="engagement-refresh-confirm" color="inherit" size="small" onClick={() => confirmRefresh(preview.queryId)}>Refresh now</Button>}>
          Exact query: <strong>{preview.data?.exactQuery ?? 'mentions'}</strong>. Estimated maximum cost: ${Number(preview.data?.provider?.estimatedMaximumCostUsd ?? 0).toFixed(4)}. The preview made no X request.
        </Alert>
      )}
      {preview?.kind === 'reply' && (
        <Alert data-testid="engagement-reply-preview" severity="warning" action={<Button data-testid="engagement-reply-confirm" color="inherit" size="small" startIcon={<SendIcon />} onClick={() => confirmReply(preview.itemId, preview.data.confirmation)}>Send approved reply</Button>}>
          Reply to {preview.data.replyToPostId}: {preview.data.text}. This action will publish immediately; it is not scheduled.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }} data-testid="engagement-listening-sources">
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Saved listening sources</Typography>
          <Button data-testid="engagement-add-source" startIcon={<AddIcon />} onClick={() => setQueryDialog(true)}>Add source</Button>
        </Stack>
        <Stack spacing={1}>
          {queries.length === 0 && <Typography variant="body2" color="text.secondary">No saved sources yet.</Typography>}
          {queries.map((query) => (
            <Stack key={query.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">{query.name}</Typography>
                <Typography variant="caption" color="text.secondary">{query.kind === 'mentions' ? 'Connected-account mentions' : query.query ?? `from:${query.trackedUsername}`}</Typography>
              </Box>
              <Chip size="small" label={query.kind.replace('_', ' ')} />
              <Button data-testid={`engagement-refresh-${query.id}`} size="small" startIcon={<RefreshIcon />} disabled={working} onClick={() => previewRefresh(query.id)}>Refresh now</Button>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }} data-testid="engagement-queue">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>Human review queue</Typography>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select data-testid="engagement-status-filter" label="Status" value={status} onChange={(event) => setStatus(event.target.value)}>
              <MenuItem value="">All</MenuItem>
              {['pending', 'approved', 'dismissed', 'published', 'needs_reconciliation'].map((value) => <MenuItem key={value} value={value}>{value.replace('_', ' ')}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        <Stack spacing={1.5}>
          {items.length === 0 && <Typography variant="body2" color="text.secondary">No engagement candidates for this filter.</Typography>}
          {items.map((item) => (
            <Box key={item.id} data-testid={`engagement-item-${item.id}`} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" color={item.sourceType === 'mention' ? 'info' : 'default'} label={item.sourceType === 'mention' ? 'Higher-intent mention' : item.sourceType.replace('_', ' ')} />
                <Chip size="small" variant="outlined" label={item.status} />
                {item.evidence && <Chip size="small" variant="outlined" label={`Match ${item.evidence.score}`} />}
              </Stack>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{item.sourceText}</Typography>
              {item.evidence?.reasons?.length ? <Typography variant="caption" color="text.secondary">{item.evidence.reasons.join(' ')}</Typography> : null}
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                <Button size="small" href={item.sourcePostUrl} target="_blank" rel="noreferrer" startIcon={<OpenInNewIcon />}>Open in X</Button>
                {item.status !== 'dismissed' && item.status !== 'published' && (
                  <Button data-testid={`engagement-approve-${item.id}`} size="small" onClick={() => { setApprovalItem(item); setReplyText(item.editedReply ?? item.suggestedReply ?? ''); }}>Edit & approve</Button>
                )}
                {item.status === 'approved' && <Button data-testid={`engagement-reply-${item.id}`} size="small" color="warning" startIcon={<SendIcon />} onClick={() => previewReply(item)}>Reply now</Button>}
                {item.status === 'needs_reconciliation' && <Button data-testid={`engagement-reconcile-${item.id}`} size="small" startIcon={<RefreshIcon />} onClick={() => reconcileReply(item.id)}>Reconcile</Button>}
                {item.status !== 'dismissed' && item.status !== 'published' && <Button data-testid={`engagement-dismiss-${item.id}`} size="small" color="inherit" onClick={() => run(async () => { await service.dismiss(projectId, item.id); await load(); })}>Dismiss</Button>}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }} data-testid="x-analytics-comparison">
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Stored analytics comparison</Typography>
            <Typography variant="caption" color="text.secondary">
              Uses {analyticsComparison.publicationsAnalyzed} stored publication snapshots. Changing this view never contacts X.
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Group by</InputLabel>
            <Select data-testid="x-analytics-group-by" label="Group by" value={analyticsGroupBy} onChange={(event) => setAnalyticsGroupBy(event.target.value as AnalyticsGroupBy)}>
              <MenuItem value="pillar">Pillar</MenuItem>
              <MenuItem value="series">Series</MenuItem>
              <MenuItem value="audience">Audience</MenuItem>
              <MenuItem value="post_type">Post type</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack spacing={1}>
          {analyticsComparison.groups.length === 0 && <Typography variant="body2" color="text.secondary">No stored X analytics snapshots yet.</Typography>}
          {analyticsComparison.groups.map((group) => (
            <Stack key={group.key} data-testid={`x-analytics-group-${group.key}`} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>{group.key}</Typography>
              <Chip size="small" label={`${group.publicationCount} posts`} />
              {typeof group.metrics.impressions === 'number' && <Chip size="small" variant="outlined" label={`${group.metrics.impressions} impressions`} />}
              {typeof group.metrics.engagements === 'number' && <Chip size="small" variant="outlined" label={`${group.metrics.engagements} engagements`} />}
              {typeof group.engagementRate === 'number' && <Chip size="small" color="info" variant="outlined" label={`${(group.engagementRate * 100).toFixed(1)}% engagement`} />}
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Dialog data-testid="engagement-source-dialog" open={queryDialog} onClose={() => setQueryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add listening source</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Name" value={queryDraft.name} onChange={(event) => setQueryDraft((draft) => ({ ...draft, name: event.target.value }))} />
          <TextField select label="Kind" value={queryDraft.kind} onChange={(event) => setQueryDraft((draft) => ({ ...draft, kind: event.target.value }))}>
            <MenuItem value="search">Saved search</MenuItem><MenuItem value="mentions">Mentions</MenuItem><MenuItem value="tracked_account">Tracked account</MenuItem>
          </TextField>
          {queryDraft.kind === 'search' && <TextField label="Exact X query" value={queryDraft.query} onChange={(event) => setQueryDraft((draft) => ({ ...draft, query: event.target.value }))} />}
          {queryDraft.kind === 'tracked_account' && <TextField label="X username" value={queryDraft.trackedUsername} onChange={(event) => setQueryDraft((draft) => ({ ...draft, trackedUsername: event.target.value }))} />}
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setQueryDialog(false)}>Cancel</Button><Button variant="contained" disabled={!queryDraft.name || working} onClick={saveQuery}>Save</Button></DialogActions>
      </Dialog>

      <Dialog data-testid="engagement-approval-dialog" open={Boolean(approvalItem)} onClose={() => setApprovalItem(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit and approve reply</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">Approval stores a hash of this exact text and target. Editing either later makes approval stale.</Alert>
          <TextField inputProps={{ 'data-testid': 'engagement-reply-text' }} multiline minRows={4} label="Reply text" value={replyText} onChange={(event) => setReplyText(event.target.value)} />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setApprovalItem(null)}>Cancel</Button><Button data-testid="engagement-approval-confirm" variant="contained" disabled={!replyText.trim() || working} onClick={approve}>Approve</Button></DialogActions>
      </Dialog>
    </Stack>
  );
};

export default SocialEngagementPanel;
